import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Search, MapPin, Building2, Globe, ExternalLink } from 'lucide-react';

const LinkCard = ({ href, icon: Icon, iconBg, iconColor, title, subtitle }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
    <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
      <Icon className={`w-5 h-5 ${iconColor}`} />
    </div>
    <div>
      <p className="font-medium text-slate-900">{title}</p>
      <p className="text-xs text-slate-500">{subtitle}</p>
    </div>
    <ExternalLink className="w-4 h-4 text-slate-400 ml-auto" />
  </a>
);

export const PropertyLookupDialog = ({ open, onOpenChange, llc }) => {
  const addr = encodeURIComponent(llc?.property_address || '');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl flex items-center gap-2">
            <Search className="w-5 h-5" />
            Property Lookup
          </DialogTitle>
          <DialogDescription>Search for property information on external sites</DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between">
              <p className="font-medium text-slate-900">{llc?.property_address}</p>
              {llc?.property_type && (
                <Badge variant="outline" className={llc.property_type === 'commercial' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}>
                  {llc.property_type === 'commercial' ? 'Commercial' : 'Residential'}
                </Badge>
              )}
            </div>
            {llc?.property_description && <p className="text-sm text-slate-500 mt-1">{llc.property_description}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <LinkCard href={`https://www.google.com/maps/search/${addr}`} icon={MapPin} iconBg="bg-red-100" iconColor="text-red-600" title="Google Maps" subtitle="Street view & map" />
            <LinkCard href={`https://www.google.com/search?q=${addr}+property+records`} icon={Search} iconBg="bg-purple-100" iconColor="text-purple-600" title="Google Search" subtitle="Property records" />

            {llc?.property_type !== 'residential' && (
              <>
                <LinkCard href={`https://www.loopnet.com/search/commercial-real-estate/${addr}/for-lease/`} icon={Building2} iconBg="bg-green-100" iconColor="text-green-600" title="LoopNet" subtitle="Commercial listings" />
                <LinkCard href="https://www.costar.com/" icon={Building2} iconBg="bg-indigo-100" iconColor="text-indigo-600" title="CoStar" subtitle="Commercial data" />
              </>
            )}

            {llc?.property_type !== 'commercial' && (
              <>
                <LinkCard href={`https://www.zillow.com/homes/${addr}_rb/`} icon={Globe} iconBg="bg-blue-100" iconColor="text-blue-600" title="Zillow" subtitle="Residential values" />
                <LinkCard href={`https://www.realtor.com/realestateandhomes-search/${encodeURIComponent(llc?.property_address?.replace(/,/g, '').replace(/\s+/g, '-') || '')}`} icon={Building2} iconBg="bg-amber-100" iconColor="text-amber-600" title="Realtor.com" subtitle="Property details" />
                <LinkCard href={`https://www.redfin.com/search?q=${addr}`} icon={Building2} iconBg="bg-rose-100" iconColor="text-rose-600" title="Redfin" subtitle="Home estimates" />
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyLookupDialog;
